import * as React from "react";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import {
  Alert,
  Button,
  FlexRow,
  IconArrow,
  IconPencilEdit,
  IconPlusThick,
  Link,
  Stack,
  Text,
} from "atoms";
import { GenericImage, type Image } from "features/images";
import type { FeaturedGalleryItem } from "features/reviewable";
import { preventForwarding } from "styles/styleUtils";
import { paths } from "utils/paths";

type FeaturedGalleryItemsProps = {
  featuredItems?: (FeaturedGalleryItem | null)[];
  artistId: string;
  editFeaturedItem?: (index: FeaturedItemIndex) => void;
};

const defaultFeaturedItems = [null, null, null, null];

function FeaturedGalleryItems({
  artistId,
  featuredItems = defaultFeaturedItems,
  editFeaturedItem,
}: FeaturedGalleryItemsProps) {
  const { pathname } = useLocation();
  const isEditing = pathname.endsWith("/edit");

  const defaultImg = React.useMemo(
    () => featuredItems.find((item) => item?.image)?.image,
    [featuredItems]
  );

  const showMessageToFinish =
    !!editFeaturedItem && featuredItems.some((item) => item === null);

  return (
    <Stack gap="sm">
      {showMessageToFinish && (
        <Alert type="warning">
          <Stack gap="xxs">
            <Text size="xs" bold>
              Finish Filling Out Your Featured Work!
            </Text>

            <Text size="xs">
              Please finish selecting all four featured images in order for them
              to display on mobile.
            </Text>
          </Stack>
        </Alert>
      )}
      <FeaturedGrid>
        <FeaturedItem
          index={0}
          artistId={artistId}
          featuredItems={featuredItems}
          defaultImg={defaultImg}
          editFeaturedItem={editFeaturedItem}
          isEditing={isEditing}
        />

        <FeaturedGridRight>
          <FeaturedGridRightTop>
            <FeaturedItem
              index={1}
              artistId={artistId}
              featuredItems={featuredItems}
              defaultImg={defaultImg}
              editFeaturedItem={editFeaturedItem}
              isEditing={isEditing}
            />
            <FeaturedGridRightTopRight>
              <FeaturedItem
                index={2}
                artistId={artistId}
                featuredItems={featuredItems}
                defaultImg={defaultImg}
                editFeaturedItem={editFeaturedItem}
                isEditing={isEditing}
              />
              <FeaturedItem
                index={3}
                artistId={artistId}
                featuredItems={featuredItems}
                defaultImg={defaultImg}
                editFeaturedItem={editFeaturedItem}
                isEditing={isEditing}
              />
            </FeaturedGridRightTopRight>
          </FeaturedGridRightTop>
          <FeaturedWorkCTA aria-hidden={!!isEditing}>
            <CTAImage aria-hidden src={defaultImg} />
            <CTATextLink
              to={paths.artistPortfolio(artistId)}
              variant="blank"
              size="sm"
              tabIndex={editFeaturedItem ? -1 : 0}
            >
              Explore Portfolio
            </CTATextLink>
            <Link
              to={paths.artistPortfolio(artistId)}
              variant="secondary"
              size="xxxs"
              arrow="r"
              aria-hidden
              tabIndex={-1}
              style={{
                zIndex: -2,
              }}
            >
              See more
            </Link>
          </FeaturedWorkCTA>
        </FeaturedGridRight>
      </FeaturedGrid>
    </Stack>
  );
}

const featuredItemStyles = {
  0: {
    sizes: "(max-width: 830px) 100vw, (max-width: 1440px) 50vw, 660w",
    style: {
      aspectRatio: "1",
      flex: 1,
    },
  },
  1: {
    sizes: "(max-width: 830px) 60vw, (max-width: 1440px) 33vw, 420w",
    style: {
      aspectRatio: "3 / 4",
      flex: 7.1,
    },
  },
  2: {
    sizes: "(max-width: 830px) 40vw, (max-width: 1440px) 15vw, 250w",
    style: {
      aspectRatio: "3 / 4",
      flex: 5.333,
    },
  },
  3: {
    sizes: "(max-width: 830px) 40vw, (max-width: 1440px) 15vw, 250w",
    style: {
      aspectRatio: "1",
      flex: 4,
    },
  },
} as const;

type FeaturedItemIndex = keyof typeof featuredItemStyles;

type FeaturedItemProps = {
  index: FeaturedItemIndex;
  artistId: string;
  featuredItems: (FeaturedGalleryItem | null)[];
  defaultImg?: Image;
  isEditing: boolean;
  editFeaturedItem?: (index: FeaturedItemIndex) => void;
};

function FeaturedItem({
  index,
  artistId,
  featuredItems,
  defaultImg,
  isEditing,
  editFeaturedItem,
}: FeaturedItemProps) {
  const item = featuredItems[index];
  const { sizes, style } = featuredItemStyles[index];
  const onClick = editFeaturedItem && (() => editFeaturedItem(index));

  return (
    <ItemWrapper style={style}>
      <Item
        src={item?.image ?? defaultImg?.blur ?? defaultImg?.url}
        hasImg={!!item?.image}
        sizes={sizes}
        aspectRatio={style.aspectRatio}
      />

      <FeaturedItemAction
        item={item}
        artistId={artistId}
        isEditing={isEditing}
        onClick={onClick}
      />
    </ItemWrapper>
  );
}

const ItemWrapper = styled.div({
  position: "relative",
  display: "flex",
  zIndex: 1,
  minWidth: 0,
});

const FeaturedGrid = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  gap: "1rem",
  [theme.breakpoints.desktop]: {
    flexDirection: "row",
  },
}));

const FeaturedGridRight = styled.div(({ theme }) => ({
  display: "flex",
  gap: "1rem",
  flexDirection: "column",
  maxWidth: "100%",
  minWidth: 0,
  [theme.breakpoints.desktop]: {
    aspectRatio: "1",
    flex: "0 0 calc(50% - .5rem)",
    maxWidth: "calc(50% - .5rem)",
  },
}));

const FeaturedGridRightTop = styled.div({
  display: "flex",
  flex: "0 0 auto",
  gap: "1rem",
  minWidth: 0,
});

const FeaturedGridRightTopRight = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: 4,
  gap: "1rem",
  minWidth: 0,
});

const Item = styled(GenericImage)<{ hasImg: boolean }>(({ hasImg, theme }) => ({
  borderRadius: theme.borderRadius.sm,
  alignSelf: "stretch",
  overflow: "hidden",
  minWidth: 0,
  "&, img": {
    height: "100%",
    objectFit: "cover",
    width: "100%",
  },
  ...(!hasImg && {
    img: {
      filter: "blur(40px) brightness(.8) saturate(1.5)",
      opacity: 0.5,
      transform: "scale(1.5)",
    },
  }),
}));

type FeaturedItemActionProps = {
  artistId: string;
  item: FeaturedGalleryItem | null;
  isEditing: boolean;
  onClick?: VoidFunction;
};

function FeaturedItemAction({
  onClick,
  artistId,
  item,
  isEditing,
}: FeaturedItemActionProps) {
  // In editing mode
  if (onClick && isEditing) {
    return (
      <CoverButton hasImage={!!item} onClick={onClick}>
        {item ? (
          <EditIconWrapper>
            <IconPencilEdit />
          </EditIconWrapper>
        ) : (
          <IconWrapper>
            <IconPlusThick />
          </IconWrapper>
        )}
      </CoverButton>
    );
  }
  // Not in editing mode
  if (onClick && !isEditing && !item) {
    return (
      <CoverButton hasImage={!!item} onClick={onClick}>
        <IconWrapper>
          <IconPlusThick />
        </IconWrapper>
      </CoverButton>
    );
  }

  return !item ? null : (
    <FeaturedItemLink
      to={paths.artistGalleryItem(artistId, item.galleryItemId)}
    >
      <TitleWrapper>
        <Text size="xs" as="span" font="display" bold>
          {item.title}
        </Text>
        <SIconArrow />
      </TitleWrapper>
    </FeaturedItemLink>
  );
}

const CoverButton = styled(Button, {
  shouldForwardProp: preventForwarding("hasImage"),
})<{ hasImage?: boolean }>(({ hasImage, theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: "100%",
  "&": {
    border: "0 !important",
  },
  borderRadius: theme.borderRadius.sm,
  backgroundImage: hasImage
    ? `linear-gradient(180deg, #00000080 0rem, #00000040 5rem, rgba(0,0,0,0) 8rem)`
    : "none",
}));
CoverButton.defaultProps = {
  variant: "blank",
};

const IconWrapper = styled.span(({ theme }) => ({
  backfaceVisibility: "hidden",
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.round,
  boxShadow: theme.boxShadow.light,
  color: theme.colors.black,
  display: "grid",
  height: "calc(40px + 1vw)",
  marginLeft: "auto",
  marginRight: "auto",
  placeItems: "center",
  transition: ".2s ease transform, .2s ease box-shadow",
  width: "calc(40px + 1vw)",
  "button:hover &, button:focus-visible &": {
    transform: "scale(1.1)",
    boxShadow: theme.boxShadow.dark,
  },
}));

const EditIconWrapper = styled(IconWrapper)({
  alignSelf: "start",
  justifySelf: "end",
  margin: "1rem 1rem 0 auto",
});

const FeaturedItemLink = styled(Link)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  justifySelf: "stretch",
  width: "100%",
  transition: ".3s ease opacity",
  color: theme.colors.white,
  zIndex: 2,
  "&:hover::before, &:focus-visible::before": {
    opacity: 0.666,
  },
  "&:hover div, &:focus-visible div": {
    opacity: 1,
    color: theme.colors.white,
  },
  span: {
    position: "relative",
    zIndex: 2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  padding: theme.spacing.md,
  "&::before": {
    content: "''",
    position: "absolute",
    zIndex: 0,
    inset: 0,
    opacity: 0,
    transition: ".3s ease opacity",
    backgroundImage: `
    linear-gradient(
      to bottom,
      hsla(0, 0%, 0%, 0) 0%,
      hsla(0, 0%, 0%, 0.013) 8.1%,
      hsla(0, 0%, 0%, 0.049) 15.5%,
      hsla(0, 0%, 0%, 0.104) 22.5%,
      hsla(0, 0%, 0%, 0.175) 29%,
      hsla(0, 0%, 0%, 0.259) 35.3%,
      hsla(0, 0%, 0%, 0.352) 41.2%,
      hsla(0, 0%, 0%, 0.45) 47.1%,
      hsla(0, 0%, 0%, 0.55) 52.9%,
      hsla(0, 0%, 0%, 0.648) 58.8%,
      hsla(0, 0%, 0%, 0.741) 64.7%,
      hsla(0, 0%, 0%, 0.825) 71%,
      hsla(0, 0%, 0%, 0.896) 77.5%,
      hsla(0, 0%, 0%, 0.951) 84.5%,
      hsla(0, 0%, 0%, 0.987) 91.9%,
      hsl(0, 0%, 0%) 100%
      )`,
  },
}));

const SIconArrow = styled(IconArrow)({
  width: "1em",
  flex: "0 0 1em",
});

const FeaturedWorkCTA = styled(FlexRow)(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  gap: "0 1em",
  flexWrap: "nowrap",
  gridArea: "cta",
  backgroundColor: "black",
  position: "relative",
  justifyContent: "space-between",
  minHeight: 70,
  padding: `5px ${theme.spacing.gap}`,
  overflow: "hidden",
  zIndex: 1,
  [theme.breakpoints.desktop]: {
    flex: 1,
    minHeight: "2em",
  },
  '&[aria-hidden="true"]': {
    opacity: 0.5,
    filter: "contrast(.5)",
    "&, & *, & *::before, & *::after": {
      pointerEvents: "none",
      cursor: "auto",
    },
  },
}));

const TitleWrapper = styled.div({
  opacity: 0,
  width: "100%",
  display: "flex",
  flexWrap: "nowrap",
  overflow: "hidden",
  whiteSpace: "nowrap",
  alignItems: "center",
  position: "relative",
  transition: ".3s ease opacity",
  gap: 6,
});

const CTAImage = styled(GenericImage)({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  img: {
    opacity: ".5",
    filter: "blur(10px)",
    height: "100%",
    objectFit: "cover",
    width: "100%",
    transform: "scale(2)",
    zIndex: -2,
  },
});

const CTATextLink = styled(Link)({
  "&, &:link, &:hover, &:active, &:focus, &:focus-visible, &:visited": {
    color: "white",
  },
  // border: 0,
  position: "static",
  "&:hover, &:active, &:focus, &:focus-visible": {
    borderBottomColor: "white",
  },
  "&:before": {
    content: "''",
    position: "absolute",
    inset: 0,
  },
});

export type { FeaturedItemIndex };
export { FeaturedGalleryItems, featuredItemStyles };
